/**
 * 작성자 : 홍선영
 * 날짜 : 2023.03.29
 * 기능 : YYYYMMDDHHMMSS(e.g 20220326173851) 형태의 string 날짜값을 YYYY-MM-DD HH:MM:SS로 변경하는 함수
 *
 * 수정자 : 한영광
 * 날짜 : 2023.05.11
 * 기능 : yyyymmdd(e.g 20220326) 형태의 string 날짜값을 YYYY-MM-DD로 변경하는 함수 추가
 */

export const pad = (number: number, length: number) => {
  let str = number.toString();
  while (str.length < length) {
    str = `0${str}`;
  }
  return str;
};

export const formatDate = (dateString: string) => {
  if (dateString) {
    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    const hour = dateString.slice(8, 10);
    const minuite = dateString.slice(10, 12);
    const second = dateString.slice(12, 14);

    if (!hour && !minuite && !second) return `${year}-${month}-${day}`;
    return `${year}-${month}-${day} ${hour}:${minuite}:${second}`;
  }
  return '';
};

export const getFirstDayOfMonth = (year: number, month: number) => {
  const date = new Date(year, month - 1, 1);
  const yyyy = date.getFullYear().toString();
  const MM = pad(date.getMonth() + 1, 2);
  const dd = pad(date.getDate(), 2);
  return `${yyyy}-${MM}-${dd}`;
};

export const getLastDayOfMonth = (year: number, month: number) => {
  const date = new Date(year, month, 0);
  const yyyy = date.getFullYear().toString();
  const MM = pad(date.getMonth() + 1, 2);
  const dd = pad(date.getDate(), 2);
  return `${yyyy}-${MM}-${dd}`;
};

export const formatDateYMD = (dateString: string) => {
  if (dateString) {
    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    return `${year}-${month}-${day}`;
  }
  return null;
};

export const todayYYYYMMDD = () => {
  const nowDate = new Date();
  const yyyy = nowDate.getFullYear().toString();
  const MM = pad(nowDate.getMonth() + 1, 2);
  const dd = pad(nowDate.getDate(), 2);
  return yyyy + MM + dd;
};

export const toMonthYYYMMDD = () => {
  const nowDate = new Date();
  const yyyy = nowDate.getFullYear().toString();
  const MM = pad(nowDate.getMonth() + 1, 2);
  const dd = pad(1, 2);
  return yyyy + MM + dd;
};

export const getLastDateOfMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 0부터 시작하므로 1을 더합니다.

  // 현재 달의 마지막 날
  const lastDate = new Date(year, month, 0); // 다음 달의 0번째 날 = 현재 달의 마지막 날

  // yyyymmdd 형식으로 변환
  const yyyymmdd = `${lastDate.getFullYear()}${String(lastDate.getMonth() + 1).padStart(2, '0')}${String(lastDate.getDate()).padStart(2, '0')}`;

  return yyyymmdd;
};

export const getLastDateOfNextMonthAsString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const nextMonth = today.getMonth() + 1; // 다음 달

  // 다음 달의 마지막 날 구하기
  const lastDateOfNextMonth = new Date(year, nextMonth + 1, 0);

  // 날짜를 YYYY-MM-DD 형식으로 변환
  const yearStr = lastDateOfNextMonth.getFullYear();
  const monthStr = String(lastDateOfNextMonth.getMonth() + 1).padStart(2, '0'); // 0부터 시작하므로 +1
  const dayStr = String(lastDateOfNextMonth.getDate()).padStart(2, '0');

  return `${yearStr}${monthStr}${dayStr}`; // YYYY-MM-DD 형식으로 반환
};

export const today = () => {
  const nowDate = new Date();
  const yyyy = nowDate.getFullYear().toString();
  const MM = pad(nowDate.getMonth() + 1, 2);
  const dd = pad(nowDate.getDate(), 2);
  return `${yyyy}-${MM}-${dd}`;
};

export const getCurrentDateTimeString = (): string => {
  const now = new Date();

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0'); // 월: 0~11 → 1~12
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');

  return `${yyyy}${mm}${dd}${hh}${min}`;
};

// Format a string of form "YYYYMMDDHHMMSS" into a JavaScript Date object
export const formatDateType = (str: any) => {
  if (!str || str.length !== 14) return null;
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(str.slice(6, 8), 10);
  const hours = parseInt(str.slice(8, 10), 10);
  const minutes = parseInt(str.slice(10, 12), 10);
  const seconds = parseInt(str.slice(12, 14), 10);
  return new Date(year, month, day, hours, minutes, seconds);
};

export const formatDateToStr = (date: Date) => {
  if (!date) {
    return '';
  }
  const Date = date;
  const yyyy = Date.getFullYear().toString();
  const MM = pad(Date.getMonth() + 1, 2);
  const dd = pad(Date.getDate(), 2);
  return yyyy + MM + dd;
};

export const formatDateToStrBar = (date: Date) => {
  if (!date) {
    return '';
  }
  const Date = date;
  const yyyy = Date.getFullYear().toString();
  const MM = pad(Date.getMonth() + 1, 2);
  const dd = pad(Date.getDate(), 2);
  return `${yyyy}-${MM}-${dd}`;
};

export const formatDateStringToStrBar = (dateStr: string) => {
  if (!dateStr) {
    return '';
  }
  if (dateStr.length === 8) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6)}`;
  }
  return dateStr;
};

export const todayDiffDay = (date: any) => {
  if (!date) return true;
  const nowDate = new Date();
  const yyyy = nowDate.getFullYear().toString();
  const MM = pad(nowDate.getMonth() + 1, 2);
  const dd = pad(nowDate.getDate(), 2);
  return Number(date) > Number(`${yyyy}${MM}${dd}`);
};

export const diffDay = (oldDate: any, newDate: any) => {
  const date1 = new Date(oldDate);
  const date2 = new Date(newDate);
  let diff = Math.abs(date2.getTime() - date1.getTime());
  diff = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return diff;
};

export const diffTime = (time: any) => {
  const hours = Math.floor(time / 3600); // 시간
  const remainingSecondsAfterHours = time % 3600;
  const min = Math.floor(remainingSecondsAfterHours / 60); // 분
  const sec = remainingSecondsAfterHours % 60; // 초
  return { hours, min, sec };
  // return `${hours}시간 ${min}분 ${sec}초`;
};

export const isValidYYYYMMDD = (str: string) => {
  if (str?.length !== 8 || !/^\d{8}$/.test(str)) return false;

  const year = parseInt(str.substring(0, 4), 10);
  const month = parseInt(str.substring(4, 6), 10);
  const day = parseInt(str.substring(6, 8), 10);

  if (month < 1 || month > 12) return false;

  if (month === 2) {
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return (isLeapYear && day >= 1 && day <= 29) || (!isLeapYear && day >= 1 && day <= 28);
  }

  if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
    return day >= 1 && day <= 31;
  }

  if ([4, 6, 9, 11].includes(month)) {
    return day >= 1 && day <= 30;
  }

  return false;
};

// YYYY-MM-DD HH:MM:SS 형식을 MM-DD HH:MM:SS으로 변환
export const formatMMDDHHMMSS = (str: string) => {
  if (str) return `${str?.substring(5, 10)} ${str?.substring(11)}`;
  return null;
};

// YYYY-MM-DD HH:MM:SS 문자열에서 날짜값만 반환
export const getYYYYMMDD = (str: string | undefined) => {
  if (!str) return '';
  return `${str?.substring(0, 10)}`;
};

// YYYY-MM-DD HH:MM:SS 문자열에서 시간값만 반환
export const getHHMMSS = (str: string | undefined) => {
  if (!str) return '';
  return `${str?.substring(11, 20)}`;
};

// HHMM 문자열을 HH:MM문자열로 변환
export const formatTimeString = (str: string | undefined) => {
  if (!str || str.length !== 4) return '';
  return `${str?.substring(0, 2)}:${str?.substring(2, 4)}`;
};

// YYYYMMDDHHMMSS 형식의 문자열에서 시간값만 HH:MM:SS 형식으로 변환
export const formatTimeFromYYYYMMDDHHMMSS = (str: string | undefined) => {
  if (!str || str.length !== 14) return '';
  const hour = str.substring(8, 10);
  const minute = str.substring(10, 12);
  const second = str.substring(12, 14);
  return `${hour}:${minute}:${second}`;
};

export const formatDateToYYYYMMDDHHMMSS = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}${seconds}`;
};

export const getDayStartAndEndTimeStamps = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const day = String(date.getDate()).padStart(2, '0');
  const todayString = `${year}-${month}-${day}`;

  return { start: `${todayString} 00:00:00`, end: `${todayString} 23:59:59` };
};

export const parseTime = (timeStr: string) => {
  const [time, _zone] = timeStr.split('+');
  const [h, m, s] = time.split(':').map(Number);
  return { h, m, s };
};

interface Time {
  h: number;
  m: number;
  s: number;
}

export const formatTime = (time: Time) => {
  return {
    h: time.h,
    m: time.m,
    s: time.s,
  };
};

// 1초 더하기
export const addOneSecond = (time: Time) => {
  let { h, m, s } = time;
  s += 1;
  if (s >= 60) {
    s = 0;
    m += 1;
    if (m >= 60) {
      m = 0;
      h += 1;
    } else {
      m -= 1;
    }
  } else {
    s -= 1;
  }
  return { h, m, s };
};

// 1초 빼기
export const subtractOneSecond = (time: Time) => {
  let { h, m, s } = time;
  s -= 1;
  if (s < 0) {
    s = 59;
    m -= 1;
    if (m < 0) {
      m = 59;
      h -= 1;
    }
  }
  return { h, m, s };
};

export const isOverOneYear = (dateString: string) => {
  // Parse the input date
  const year = parseInt(dateString.slice(0, 4), 10);
  const month = parseInt(dateString.slice(4, 6), 10) - 1; // Month is 0-based
  const day = parseInt(dateString.slice(6, 8), 10);
  const inputDate = new Date(year, month, day);

  // Get the current date, 1 year ago
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  // Compare dates
  return inputDate <= oneYearAgo;
};

/**
 * 알람 종료 시간을 YYYYMMDDHHmmss 형식으로 반환하는 함수
 * @param alarmOffPeriod 초 단위 (기본 옵션: 30분, 1시간 등), 사용자 지정은 -1
 * @param customTime 사용자 지정 시간 (hhmm 형태, 예: '1830'), alarmOffPeriod가 -1일 때 사용
 */
export const getAlarmOffUntil = (alarmOffPeriod: number, customTime?: string): string => {
  let targetDate: Date;

  if (alarmOffPeriod === -1 && customTime) {
    // 사용자 지정 시간: 오늘 날짜 + hhmm
    const now = new Date();
    const hour = parseInt(customTime.slice(0, 2), 10);
    const minute = parseInt(customTime.slice(2, 4), 10);
    targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
  } else if (alarmOffPeriod > 0) {
    // 기본 옵션: 현재 시간 + 지정 초
    targetDate = new Date(Date.now() + alarmOffPeriod * 1000);
  } else {
    return ''; // 비활성 또는 잘못된 값
  }

  const padFn = (n: number) => n.toString().padStart(2, '0');

  return `${targetDate.getFullYear()}${padFn(targetDate.getMonth() + 1)}${padFn(targetDate.getDate())}${padFn(targetDate.getHours())}${padFn(targetDate.getMinutes())}${padFn(
    targetDate.getSeconds()
  )}`;
};

// HHMM 형식의 문자열을 시간 객체{h: number, m: number, s: number}로 변환
export const parseTimeFromHHMM = (timeStr: string) => {
  if (!timeStr || timeStr.length !== 4) return null;

  return {
    h: parseInt(timeStr.substring(0, 2), 10),
    m: parseInt(timeStr.substring(2, 4), 10),
    s: 0,
  };
};
